import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    ArrowLeftIcon,
    CheckIcon,
    MagnifyingGlassIcon,
    RectangleGroupIcon,
    FilmIcon,
    TvIcon,
    ArrowPathRoundedSquareIcon,
    ServerIcon,
} from '@heroicons/react/24/outline';

const ModalVideoVideoWall = () => {
    const { idMaster, pinMaster } = useParams();
    const navigate = useNavigate();

    // States pour les données
    const [videoWallData, setVideoWallData] = useState([]);
    const [clientsList, setClientsList] = useState([]); // eslint-disable-line no-unused-vars
    const [videoList, setVideoList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // State pour la répétition globale
    const [globalRepetition, setGlobalRepetition] = useState('');
    // Nombre de POSITIONS vidéo affichées (partagé par tous les écrans du mur). Init depuis les
    // données, extensible via "+ Ajouter une vidéo" (X vidéos, au-delà des 5 colonnes historiques).
    const [videoCount, setVideoCount] = useState(1);

    // States pour le sélecteur de vidéo
    const [showVideoSelector, setShowVideoSelector] = useState(false);
    const [currentEditTarget, setCurrentEditTarget] = useState({ rowIndex: null, pos: null });
    const [searchTerm, setSearchTerm] = useState('');

    // --- States pour la PAGINATION ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    const searchInputRef = useRef(null);

    const token = localStorage.getItem("token");
    const headers = { 'x-access-token': token, 'Content-Type': 'application/json' };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };
    // Nom de fichier depuis une URL (affichage lisible sous chaque champ).
    const videoName = (url) => {
        const s = String(url || '').split('?')[0];
        return s.substring(s.lastIndexOf('/') + 1) || '';
    };

    // --- 1. Chargement des données ---
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const groupRes = await axios.post(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/get_group_programmes`, {
                    IdMaster: idMaster, PinMaster: pinMaster
                });
                setVideoWallData(groupRes.data);
                if (groupRes.data && groupRes.data.length > 0) {
                    setGlobalRepetition(groupRes.data[0].repetition || '');
                }

                const clientRes = await axios.post(`${process.env.REACT_APP_IP2}:${process.env.REACT_APP_PORT2}/admin/auth/listeUsers`, { token }, { headers });
                setClientsList(clientRes.data);

                const videoRes = await axios.get(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/listVideos`);
                if (videoRes.data && videoRes.data.files) {
                    setVideoList(videoRes.data.files.filter((f) => f.fileName.endsWith('.mp4')));
                }
            } catch (err) {
                console.error("Erreur chargement videowall:", err);
                toast.error("Impossible de charger les données.");
            } finally {
                setIsLoading(false);
            }
        };
        if (idMaster && pinMaster) fetchData();
    }, [idMaster, pinMaster, token]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { setCurrentPage(1); }, [searchTerm, showVideoSelector]);
    useEffect(() => { if (showVideoSelector && searchInputRef.current) searchInputRef.current.focus(); }, [showVideoSelector]);

    // Nombre de colonnes vidéo à afficher = plus grande position utilisée (ne rétrécit jamais,
    // pour conserver les colonnes que l'utilisateur vient d'ajouter). Min 1.
    useEffect(() => {
        if (!videoWallData.length) return;
        const used = Math.max(1, ...videoWallData.map(positionsUsed));
        setVideoCount((c) => Math.max(c, used));
    }, [videoWallData]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- 2. Gestion des modifications ---
    const handleInputChange = (index, field, value) => {
        const newData = [...videoWallData];
        newData[index] = { ...newData[index], [field]: value };
        setVideoWallData(newData);
    };
    const openVideoSelector = (index, pos) => {
        setSearchTerm('');
        setCurrentEditTarget({ rowIndex: index, pos });
        setShowVideoSelector(true);
    };
    const handleVideoSelect = (url) => {
        if (currentEditTarget.rowIndex !== null && currentEditTarget.pos) {
            setVideoAt(currentEditTarget.rowIndex, currentEditTarget.pos, url);
        }
        setShowVideoSelector(false);
        setCurrentEditTarget({ rowIndex: null, pos: null });
    };

    // --- Filtrage / pagination du sélecteur ---
    const filteredVideos = videoList.filter(v => v.fileName.toLowerCase().includes(searchTerm.toLowerCase()));
    const indexOfLastItem = currentPage * itemsPerPage;
    const currentVideos = filteredVideos.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);
    const totalPages = Math.ceil(filteredVideos.length / itemsPerPage) || 1;
    const paginate = (n) => setCurrentPage(n);

    // --- 3. Sauvegarde globale ---
    const handleGlobalSave = async () => {
        setSaving(true);
        try {
            const rep = buildRepetition();
            await Promise.all(videoWallData.map(prog => axios.put(
                `${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/edit_group_programmes`,
                {
                    ...prog,
                    Videourl1: prog.Videourl1 || '', Videourl2: prog.Videourl2 || '', Videourl3: prog.Videourl3 || '',
                    Videourl4: prog.Videourl4 || '', Videourl5: prog.Videourl5 || '',
                    videosJson: buildScreenVideosJson(prog), // vidéos 6+ propres à cet écran
                    repetition: rep, lastUpdate: new Date(),
                }
            )));
            toast.success(`${videoWallData.length} écran(s) mis à jour.`);
        } catch (error) {
            console.error("Erreur sauvegarde globale:", error);
            toast.error("Erreur lors de la sauvegarde.");
        } finally {
            setSaving(false);
        }
    };

    const programName = (videoWallData.length > 0 && videoWallData[0].Subject) ? videoWallData[0].Subject : 'Mur d\'écrans';

    // Le SERVEUR du mur = l'écran dont le PIN vaut le PinMaster du groupe (les clients ont un
    // autre _pin_code). Mis en avant visuellement + placé en tête.
    const isServer = (prog) => String(prog._pin_code || '').trim() === String(prog.PinMaster || pinMaster || '').trim();
    // NB : la propriété DOIT s'appeler `index` (et pas `idx`) — le rendu déstructure `{ prog, index }`.
    // Avec `idx`, `index` valait `undefined` partout -> setVideoAt(undefined,…) n'écrivait sur aucune
    // ligne réelle : la sélection du picker (et la saisie manuelle d'URL) n'ajoutait rien, et les
    // libellés affichaient « Écran NaN ». `index` = position d'origine dans videoWallData (conservée
    // après le tri serveur-en-tête), donc setVideoAt cible bien le bon écran.
    const orderedRows = videoWallData
        .map((prog, index) => ({ prog, index }))
        .sort((a, b) => (isServer(b.prog) ? 1 : 0) - (isServer(a.prog) ? 1 : 0));

    // --- X vidéos : accès positionnel (pos 1..5 = Videourl1..5 ; pos 6+ = videosJson[pos-6]) ---
    const parseVJ = (raw) => { try { const a = JSON.parse(raw || '[]'); return Array.isArray(a) ? a.map(String) : []; } catch (e) { return []; } };
    const positionsUsed = (prog) => {
        let m = 0;
        for (let p = 1; p <= 5; p++) if (String(prog[`Videourl${p}`] || '').trim()) m = p;
        const vj = parseVJ(prog.videosJson);
        for (let i = 0; i < vj.length; i++) if (String(vj[i] || '').trim()) m = Math.max(m, 6 + i);
        return m;
    };
    const videoAt = (prog, pos) => {
        if (pos <= 5) return prog[`Videourl${pos}`] || '';
        return parseVJ(prog.videosJson)[pos - 6] || '';
    };
    const setVideoAt = (index, pos, value) => {
        if (pos <= 5) { handleInputChange(index, `Videourl${pos}`, value); return; }
        const vj = parseVJ(videoWallData[index].videosJson);
        while (vj.length < pos - 5) vj.push('');
        vj[pos - 6] = value;
        handleInputChange(index, 'videosJson', JSON.stringify(vj));
    };
    // videosJson d'un écran pour le save : positions 6..videoCount non vides (mur = contigu).
    const buildScreenVideosJson = (prog) => {
        const arr = [];
        for (let pos = 6; pos <= videoCount; pos++) { const u = String(videoAt(prog, pos) || '').trim(); if (u) arr.push(u); }
        return arr.length ? JSON.stringify(arr) : null;
    };

    // --- Répétition PAR VIDÉO (une valeur par position, partagée par tous les écrans) ---
    const repAt = (i) => { const v = (String(globalRepetition).split(';')[i] || '').trim(); return (v && !Number.isNaN(Number(v))) ? v : '1'; };
    const setRepAt = (i, val) => {
        const arr = String(globalRepetition).split(';');
        while (arr.length <= i) arr.push('1');
        const n = parseInt(val, 10);
        arr[i] = (n && n > 0) ? String(n) : '1';
        setGlobalRepetition(arr.slice(0, videoCount).join(';'));
    };
    const buildRepetition = () => {
        const arr = String(globalRepetition).split(';');
        return Array.from({ length: videoCount }, (_, i) => { const n = parseInt((arr[i] || '').trim(), 10); return (n && n > 0) ? String(n) : '1'; }).join(';');
    };
    const totalPasses = buildRepetition().split(';').reduce((a, b) => a + (parseInt(b, 10) || 0), 0);

    const Stat = ({ icon: Icon, value, label, wrap }) => (
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${wrap}`}><Icon className="h-5 w-5" /></div>
            <div>
                <div className="text-xl font-bold tabular-nums text-slate-800">{value}</div>
                <div className="text-xs text-slate-400">{label}</div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Barre supérieure sticky */}
            <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
                <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
                    <div className="flex min-w-0 items-center gap-3">
                        <button onClick={() => navigate(-1)} title="Retour"
                            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800">
                            <ArrowLeftIcon className="h-5 w-5" />
                        </button>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-indigo-700">Mur d'écrans</span>
                                <h1 className="truncate text-lg font-semibold text-slate-800">{programName}</h1>
                            </div>
                            <p className="text-xs text-slate-400">Master <span className="font-mono">{idMaster}</span> · PIN <span className="font-mono">{pinMaster}</span></p>
                        </div>
                    </div>
                    <button onClick={handleGlobalSave} disabled={saving || isLoading}
                        className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:opacity-50">
                        <CheckIcon className="h-4 w-4" />
                        {saving ? 'Enregistrement…' : 'Tout enregistrer'}
                    </button>
                </div>
            </div>

            <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6">
                {isLoading ? (
                    <div className="py-24 text-center text-slate-400">Chargement du mur…</div>
                ) : (
                    <>
                        {/* Statistiques */}
                        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
                            <Stat icon={TvIcon} value={videoWallData.length} label="écrans dans le mur" wrap="bg-indigo-500/10 text-indigo-600" />
                            <Stat icon={FilmIcon} value={videoCount} label="vidéos par écran" wrap="bg-sky-500/10 text-sky-600" />
                            <Stat icon={ArrowPathRoundedSquareIcon} value={totalPasses} label="passages / boucle" wrap="bg-emerald-500/10 text-emerald-600" />
                        </div>

                        {/* Tableau des écrans */}
                        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                            <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
                                <RectangleGroupIcon className="h-5 w-5 text-slate-400" />
                                <h2 className="text-sm font-semibold text-slate-800">Vidéos par écran</h2>
                                <span className="text-xs text-slate-400">— chaque écran diffuse sa propre découpe du mur</span>
                                <button
                                    type="button"
                                    onClick={() => setVideoCount((c) => Math.min(50, c + 1))}
                                    className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-dashed border-indigo-300 px-3 py-1.5 text-xs font-medium text-indigo-600 transition hover:bg-indigo-50"
                                    title="Ajoute une colonne vidéo à tous les écrans (au-delà de 5 : nécessite app ≥ 5.2.7)"
                                >
                                    + Ajouter une vidéo
                                </button>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <th className="whitespace-nowrap px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Écran</th>
                                            {Array.from({ length: videoCount }, (_, i) => (
                                                <th key={i} className="min-w-[230px] px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                    Vidéo {i + 1}{i >= 5 && <span className="ml-1 rounded bg-amber-100 px-1 text-[9px] font-bold text-amber-700">5.2.7+</span>}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orderedRows.map(({ prog, index }) => {
                                            const server = isServer(prog);
                                            return (
                                            <tr key={prog.Id || index}
                                                className={`border-t transition-colors ${server ? 'border-indigo-200 bg-indigo-50/60 hover:bg-indigo-50' : 'border-slate-100 hover:bg-indigo-50/30'}`}>
                                                <td className={`px-5 py-3 align-top ${server ? 'border-l-4 border-l-indigo-500' : ''}`}>
                                                    <div className="flex items-center gap-2.5">
                                                        {server ? (
                                                            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-indigo-600 text-white shadow-sm">
                                                                <ServerIcon className="h-4 w-4" />
                                                            </span>
                                                        ) : (
                                                            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-slate-100 text-xs font-bold text-slate-500">{index + 1}</span>
                                                        )}
                                                        <div>
                                                            <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                                                                {server ? 'Serveur' : `Écran ${index + 1}`}
                                                                {server && <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Maître</span>}
                                                            </div>
                                                            <div className="font-mono text-[11px] text-slate-400">PIN {prog._pin_code}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                {Array.from({ length: videoCount }, (_, i) => {
                                                    const pos = i + 1;
                                                    const url = videoAt(prog, pos);
                                                    const name = videoName(url);
                                                    return (
                                                        <td key={i} className="px-4 py-3 align-top">
                                                            <div className="relative">
                                                                <input
                                                                    type="text"
                                                                    value={url}
                                                                    title={url}
                                                                    onChange={(e) => setVideoAt(index, pos, e.target.value)}
                                                                    placeholder="Coller une URL ou choisir…"
                                                                    className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-3 pr-9 text-xs text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                                                                />
                                                                <button onClick={() => openVideoSelector(index, pos)} title="Choisir dans la médiathèque"
                                                                    className="absolute inset-y-0 right-0 grid w-9 place-items-center rounded-r-lg text-slate-400 transition hover:text-indigo-600">
                                                                    <MagnifyingGlassIcon className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                            <div className="mt-1 flex items-center gap-1.5 truncate text-[11px]">
                                                                {name ? (
                                                                    <>
                                                                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                                                                        <span className="truncate font-medium text-slate-500" title={name}>{name}</span>
                                                                    </>
                                                                ) : (
                                                                    <span className="text-slate-300">Aucune vidéo</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );})}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Répétition par vidéo (synchro sur tout le mur) */}
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <div className="flex items-center gap-2">
                                <ArrowPathRoundedSquareIcon className="h-5 w-5 text-slate-400" />
                                <h2 className="text-sm font-semibold text-slate-800">Répétition par vidéo</h2>
                            </div>
                            <p className="mt-1 text-xs text-slate-400">
                                Nombre de passages de chaque vidéo dans la boucle — appliqué à <b>tous les écrans</b> (lecture synchronisée).
                            </p>
                            <div className="mt-5 flex flex-wrap items-end gap-3">
                                {Array.from({ length: videoCount }, (_, i) => (
                                    <div key={i} className="flex flex-col items-center gap-1.5">
                                        <span className="text-xs font-semibold text-slate-500">Vidéo {i + 1}</span>
                                        <div className="flex items-center gap-1 rounded-lg border border-slate-300 bg-slate-50 px-2.5 py-1.5 transition focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20">
                                            <span className="select-none text-sm text-slate-400">×</span>
                                            <input type="number" min="1" value={repAt(i)} onChange={(e) => setRepAt(i, e.target.value)}
                                                className="w-11 bg-transparent text-center text-lg font-mono font-semibold text-slate-800 outline-none" />
                                        </div>
                                    </div>
                                ))}
                                <div className="ml-auto flex items-center gap-2 self-center text-[11px] text-slate-400">
                                    <span>envoyé :</span>
                                    <code className="rounded bg-slate-100 px-2 py-0.5 font-mono text-slate-600">{buildRepetition()}</code>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* --- Sélecteur de vidéo --- */}
            {showVideoSelector && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
                    <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
                        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                            <div className="flex items-center gap-2">
                                <FilmIcon className="h-5 w-5 text-indigo-500" />
                                <h3 className="text-base font-semibold text-slate-800">Choisir une vidéo</h3>
                            </div>
                            <button onClick={() => setShowVideoSelector(false)} className="grid h-8 w-8 place-items-center rounded-lg text-lg leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700">✕</button>
                        </div>

                        <div className="border-b border-slate-100 p-4">
                            <div className="relative flex h-10 items-center rounded-xl border border-slate-200 bg-white focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20">
                                <MagnifyingGlassIcon className="ml-3 h-5 w-5 text-slate-400" />
                                <input ref={searchInputRef} type="text" placeholder="Rechercher une vidéo…" value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="h-full w-full rounded-xl bg-transparent px-3 text-sm text-slate-800 placeholder-slate-400 outline-none" />
                            </div>
                        </div>

                        <div className="min-h-[280px] flex-grow overflow-y-auto">
                            {currentVideos.length > 0 ? (
                                <ul className="divide-y divide-slate-100">
                                    {currentVideos.map((video, idx) => (
                                        <li key={idx} onClick={() => handleVideoSelect(process.env.REACT_APP_DL_IP + 'MOV/' + video.fileName)}
                                            className="group flex cursor-pointer items-center justify-between gap-3 px-5 py-3 transition hover:bg-indigo-50/50">
                                            <div className="flex min-w-0 items-center gap-3">
                                                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-400 group-hover:bg-indigo-100 group-hover:text-indigo-600">
                                                    <FilmIcon className="h-5 w-5" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="truncate text-sm font-medium text-slate-700" title={video.fileName}>{video.fileName}</div>
                                                    <div className="mt-0.5 flex gap-2 text-xs text-slate-400">
                                                        <span>{video.fileSize}</span><span>·</span><span>{formatDate(video.fileDate)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <span className="rounded-lg bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700 opacity-0 transition group-hover:opacity-100">Choisir</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="flex h-full flex-col items-center justify-center p-10 text-slate-400">
                                    <MagnifyingGlassIcon className="mb-2 h-10 w-10 text-slate-300" />
                                    <p className="text-sm">Aucune vidéo trouvée.</p>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50 px-5 py-3">
                            <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}
                                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${currentPage === 1 ? 'cursor-not-allowed text-slate-300' : 'text-slate-600 hover:bg-slate-200'}`}>Précédent</button>
                            <span className="text-xs text-slate-500">Page {currentPage} / {totalPages}</span>
                            <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}
                                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${currentPage === totalPages ? 'cursor-not-allowed text-slate-300' : 'text-slate-600 hover:bg-slate-200'}`}>Suivant</button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer />
        </div>
    );
};

export default ModalVideoVideoWall;
